import React, { useMemo } from "react";
import type { PersonType } from "@whatslly/types/Person";
import { QUERY_RESPONSE_PERSON_TYPES } from "@whatslly/types/Person";
import { buildEntityLink } from "@whatslly/commons/entityUtils";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { getPrettyPhone } from "@whatslly/commons/utils";
import { CrmIcon } from "@whatslly/ui-components/CrmIcon";
import { RenderSvgBasedOnType } from "@whatslly/ui-components/RenderSvgBasedOnType";
import classnames from "classnames";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import styles from "./global-search-item.module.scss";
import { Checkbox } from "@whatslly/ui-components/v2/Checkbox";
import upperFirst from "lodash/upperFirst";

type onClickFn = ({ phone, id, type }: { phone?: string; id: string; type: string }) => void;

export const GlobalSearchItem: React.FC<{
  name: string | undefined;
  type: PersonType["type"];
  phones: { value: string; label: string; name: string }[];
  id: string;
  typeText: string;
  companyName?: string;
  onClickOnCrm?: VoidFunction;
  hideCrmLinks: boolean | undefined;
  baseUrl: string | undefined;
  loginProvider: LoginProviderType;
  onClickOnRow?: onClickFn;
  onClickOnMultiplePhoneRow?: onClickFn;
  wrapperClassName?: string;
  checkboxIsSelected?: boolean;
  showCheckbox?: boolean;
  ignoreMultiplePhoneSeparation?: boolean;
  menuItemNameSize?: "medium" | "small";
  viewInCrmTarget?: string;
}> = ({
  name,
  type,
  phones,
  id,
  onClickOnCrm,
  typeText,
  companyName,
  hideCrmLinks,
  baseUrl,
  loginProvider,
  onClickOnRow,
  onClickOnMultiplePhoneRow,
  wrapperClassName,
  showCheckbox,
  checkboxIsSelected,
  ignoreMultiplePhoneSeparation,
  menuItemNameSize,
  viewInCrmTarget = "sfdc",
}) => {
  const hasMultiplePhones = useMemo(() => phones.length > 1, [phones.length]);

  const showCrmIcon = useMemo(() => {
    return type !== QUERY_RESPONSE_PERSON_TYPES.UNKNOWN && !hideCrmLinks;
  }, [type, hideCrmLinks]);

  return (
    <div className={classnames(wrapperClassName, styles.wrapper)}>
      <div
        onClick={() => {
          onClickOnRow?.({ phone: phones[0]?.value, id, type });
        }}
        className={classnames(styles.globalSearchMenuItemAndCheckboxContainer, {
          [styles.withCheckbox]: showCheckbox,
          [styles.notClickable]: !onClickOnRow,
        })}
      >
        {showCheckbox && (
          <div className={styles.checkboxContainer}>
            <Checkbox
              testId={name}
              onChange={() => {
                //ignore
              }}
              value={checkboxIsSelected}
              size="large"
            />
          </div>
        )}
        <div
          className={classnames(styles.globalSearchMenuItemContainer, {
            [styles.multiplePhonesItem]: hasMultiplePhones,
            [styles.ignoreMultiplePhoneSeparation]: ignoreMultiplePhoneSeparation,
            [styles.unknowPerson]: type === QUERY_RESPONSE_PERSON_TYPES.UNKNOWN,
          })}
        >
          <div className={styles.commonData}>
            <div className={classnames(styles.menuItemDetailsContainer)}>
              <div className={classnames(styles.menuItemName, [styles[`menuItemName${upperFirst(menuItemNameSize ?? "medium")}`]])}>
                <div className={styles.name}>{name}</div>
                {companyName && (
                  <>
                    <div
                      className={classnames(
                        styles.nameAndCompanyDevider,
                        globalStyles["blc-neutral-secondary"],
                        globalStyles["brc-neutral-secondary"]
                      )}
                    ></div>
                    <div className={styles.companyName}>{companyName}</div>
                  </>
                )}
              </div>
              {!hasMultiplePhones && phones[0]?.value && (
                <div className={styles.menuItemDetails}>
                  <div
                    className={classnames(styles.detail, {
                      [globalStyles["color-neutral-primary"]]: showCrmIcon,
                      [globalStyles["color-main-primary"]]: !showCrmIcon,
                      [styles.unknownPersonDetail]: type === QUERY_RESPONSE_PERSON_TYPES.UNKNOWN,
                    })}
                  >
                    <p>{getPrettyPhone(phones[0].value)}</p>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.menuItemLabelAndOptionsContainer}>
              {showCrmIcon && (
                <>
                  <div className={styles.menuItemOptions}>
                    <a
                      target={viewInCrmTarget}
                      href={buildEntityLink({
                        baseUrl,
                        id: id,
                        type: (type ?? "").toLowerCase(),
                      })}
                      onClick={(e) => {
                        onClickOnCrm?.();
                        e.stopPropagation();
                      }}
                    >
                      <CrmIcon crm={loginProvider} />
                    </a>
                  </div>
                  <span className={classnames(styles.menuItemLabelOptionsDevider, globalStyles["blc-neutral-secondary"])}></span>
                </>
              )}
              <div className={styles.menuItemLabel}>
                <RenderSvgBasedOnType type={type} typeText={typeText} />
              </div>
            </div>
          </div>
          {hasMultiplePhones && (
            <div className={styles.multiplePhonesContainer}>
              {phones.map((phone, index) => {
                return (
                  <div className={classnames(styles.multiplePhoneDivColorProvider)} key={`${phone}_${index}`}>
                    <div
                      className={classnames(styles.multiplePhoneDiv)}
                      onClick={(e) => {
                        if (ignoreMultiplePhoneSeparation) {
                          return;
                        }
                        onClickOnMultiplePhoneRow?.({ phone: phone.value, id, type });
                        e.stopPropagation();
                      }}
                      onMouseEnter={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {getPrettyPhone(phone.value)} ({phone.label})
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className={classnames(styles.menuItemBottomDevider, globalStyles["bbc-neutral-secondary"])}></div>
    </div>
  );
};
